import React, { useState, useEffect } from 'react';
import { Table, Tag, Empty, Button, Modal, Form, Input, Space, Tooltip, Popconfirm, Spin, message } from 'antd';
import {
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';

const InvoiceUnitsTab = ({ data, onUpdate }) => {
  const [form] = Form.useForm();
  const [units, setUnits] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [defaultLoadingId, setDefaultLoadingId] = useState(null);

  useEffect(() => {
    if (data?.unit_types) {
      setUnits(data.unit_types);
    }
  }, [data]);

  const handleAdd = () => {
    setEditingUnit(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setDetailLoading(true);
    setEditingUnit(record);
    setModalVisible(true);
    try {
      const res = await financeSettingsApi.getUnitType(record.id);
      const unit = res?.data?.unit_type || res?.unit_type || record;
      form.setFieldsValue({ unit_type: unit.unit_type });
    } catch (error) {
      console.error('Error fetching unit type:', error);
      form.setFieldsValue({ unit_type: record.unit_type });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSetDefault = async (unitId) => {
    setDefaultLoadingId(unitId);
    try {
      await financeSettingsApi.setDefaultUnitType(unitId);
      message.success('واحد پیش‌فرض با موفقیت تنظیم شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error setting default unit type:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در تنظیم واحد پیش‌فرض');
      }
    } finally {
      setDefaultLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await financeSettingsApi.deleteUnitType(id);
      message.success('واحد با موفقیت حذف شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting unit type:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در حذف واحد');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingUnit) {
        await financeSettingsApi.updateUnitType(editingUnit.id, { unit_type: values.unit_type });
        message.success('واحد با موفقیت ویرایش شد');
      } else {
        await financeSettingsApi.createUnitType({ unit_type: values.unit_type });
        message.success('واحد جدید با موفقیت ایجاد شد');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingUnit(null);
      onUpdate?.();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving unit type:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در ذخیره واحد');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'نام واحد',
      dataIndex: 'unit_type',
      key: 'unit_type',
      render: (text) => (
        <div className="flex items-center gap-2">
          <AppstoreOutlined className="text-purple-500" />
          <span className="font-medium">{text}</span>
        </div>
      ),
    },
    {
      title: 'پیش‌فرض',
      dataIndex: 'default',
      key: 'default',
      width: 180,
      align: 'center',
      render: (val, record) =>
        val === 1 ? (
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 13, padding: '2px 12px' }}>
            پیش‌فرض
          </Tag>
        ) : defaultLoadingId === record.id ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} size="small" />
        ) : (
          <Tag
            color="default"
            onClick={() => handleSetDefault(record.id)}
            style={{
              cursor: 'pointer',
              fontSize: 13,
              padding: '2px 12px',
              borderStyle: 'dashed',
              transition: 'all 0.2s',
            }}
            className="hover:border-green-500 hover:text-green-600"
          >
            تنظیم پیش‌فرض
          </Tag>
        ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const isDefault = record.default === 1;
        return (
          <Space>
            <Tooltip title="ویرایش">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                className="text-blue-500 hover:text-blue-600"
              />
            </Tooltip>
            {isDefault ? (
              <Tooltip title="واحد پیش‌فرض قابل حذف نیست">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Popconfirm
                title="حذف واحد"
                description="آیا از حذف این واحد اطمینان دارید؟"
                onConfirm={() => handleDelete(record.id)}
                okText="بله، حذف کن"
                cancelText="خیر"
                okButtonProps={{ danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }}
                cancelButtonProps={{ style: { borderColor: '#d9d9d9', color: '#595959' } }}
              >
                <Tooltip title="حذف">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    className="text-red-500 hover:text-red-600"
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          className="bg-green-600 hover:bg-green-700 border-0"
          style={{ fontFamily: 'CustomFont' }}
        >
          افزودن واحد جدید
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={units}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description={<span style={{ fontFamily: 'CustomFont' }}>هیچ واحدی ثبت نشده</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        style={{ fontFamily: 'CustomFont' }}
      />

      <Modal
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUnit(null);
          form.resetFields();
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <AppstoreOutlined className="text-purple-500" />
            <span>{editingUnit ? 'ویرایش واحد' : 'افزودن واحد جدید'}</span>
          </div>
        }
        footer={null}
        centered
        width={420}
        destroyOnClose
      >
        {detailLoading ? (
          <TableSkeleton rows={1} columns={1} />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
          >
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>نام واحد</span>}
              name="unit_type"
              rules={[{ required: true, message: 'نام واحد الزامی است' }]}
            >
              <Input placeholder="مثلا: عدد، ساعت، کیلوگرم" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingUnit(null);
                  form.resetFields();
                }}
                style={{ fontFamily: 'CustomFont', borderColor: '#d9d9d9', color: '#595959' }}
              >
                انصراف
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="bg-green-600 hover:bg-green-700 border-0"
                style={{ fontFamily: 'CustomFont' }}
              >
                {editingUnit ? 'ذخیره تغییرات' : 'ایجاد'}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceUnitsTab;
